/**
 * _modifiersDesayuno.ts — Modificadores demo para la categoría Desayuno.
 *
 * Grupos reutilizables con IDs únicos por plato (prefijo des_*).
 * Modalidad local / para llevar (+$1.000 icópor) en todos los platos configurables.
 */
import type { ModifierGroup } from "@/types";

const g = (
  id: string,
  label: string,
  partial: Omit<ModifierGroup, "id" | "label">,
): ModifierGroup => ({ id, label, ...partial });

const mkModalidad = (pfx: string): ModifierGroup =>
  g(`${pfx}_modalidad`, "¿Dónde lo consume?", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: `${pfx}_local`, label: "En el local" },
      {
        id: `${pfx}_llevar`,
        label: "Para llevar",
        descripcion: "Se cobra el icópor",
        precioExtra: 1000,
      },
    ],
  });

const mkHuevos = (pfx: string): ModifierGroup =>
  g(`${pfx}_huevos`, "Huevos", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: `${pfx}_h1`, label: "Pericos" },
      { id: `${pfx}_h2`, label: "Revueltos" },
      { id: `${pfx}_h3`, label: "Tortilla" },
      { id: `${pfx}_h4`, label: "Fritos" },
    ],
  });

const mkArrozPan = (pfx: string): ModifierGroup =>
  g(`${pfx}_arroz_pan`, "Arroz o Pan", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: `${pfx}_ap1`, label: "Arroz con huevo" },
      {
        id: `${pfx}_ap2`,
        label: "Pan con huevo",
        descripcion: "1 pan",
      },
    ],
  });

const mkBebida = (pfx: string): ModifierGroup =>
  g(`${pfx}_bebida`, "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: `${pfx}_b1`, label: "Limonada de panela" },
      { id: `${pfx}_b2`, label: "Chocolate" },
      { id: `${pfx}_b3`, label: "Tinto" },
      { id: `${pfx}_b4`, label: "Café" },
      { id: `${pfx}_b5`, label: "Jugo del día" },
    ],
  });

const mkCubiertos = (pfx: string): ModifierGroup =>
  g(`${pfx}_cubiertos`, "Cubiertos", {
    tipoSeleccion: "boolean",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: `${pfx}_c1`, label: "Sí, con cubiertos" },
      { id: `${pfx}_c2`, label: "No, sin cubiertos" },
    ],
  });

const mkComentario = (pfx: string): ModifierGroup =>
  g(`${pfx}_comentario`, "Comentario para preparar", {
    tipoSeleccion: "text",
    required: false,
    placeholderTexto: "Ej: poquito arroz, sin cebolla…",
    opciones: [],
  });

/** Caldos individuales: modalidad + cubiertos + comentario. */
export const MOD_DESAYUNO_CALDO = (pfx: string): ModifierGroup[] => [
  mkModalidad(pfx),
  mkCubiertos(pfx),
  mkComentario(pfx),
];

/** @deprecated Usar MOD_DESAYUNO_CALDO */
export const MOD_DESAYUNO_MODALIDAD = MOD_DESAYUNO_CALDO;

/** 4 — Desayuno Completo (precio base $12.000; caldo define el total) */
export const MOD_DESAYUNO_COMPLETO: ModifierGroup[] = [
  mkModalidad("des_dc"),
  g("des_dc_caldo", "Caldo", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "des_dc_k1", label: "Caldo de costilla" },
      { id: "des_dc_k2", label: "Caldo de pescado" },
      { id: "des_dc_k3", label: "Caldo de pata", precioExtra: 1000 },
      { id: "des_dc_k4", label: "Caldo de pajarilla", precioExtra: 2000 },
    ],
  }),
  mkHuevos("des_dc"),
  mkArrozPan("des_dc"),
  mkBebida("des_dc"),
  mkCubiertos("des_dc"),
  mkComentario("des_dc"),
];

/** 5 — Huevos al Gusto */
export const MOD_HUEVOS_GUSTO: ModifierGroup[] = [
  mkModalidad("des_hg"),
  mkHuevos("des_hg"),
  mkArrozPan("des_hg"),
  mkBebida("des_hg"),
  mkCubiertos("des_hg"),
  mkComentario("des_hg"),
];

/** 6 — Moñona */
export const MOD_MONONA: ModifierGroup[] = [
  mkModalidad("des_mo"),
  mkHuevos("des_mo"),
  mkArrozPan("des_mo"),
  mkBebida("des_mo"),
  g("des_mo_proteina", "Proteína Moñona", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "des_mo_p1", label: "Res" },
      { id: "des_mo_p2", label: "Cerdo" },
      { id: "des_mo_p3", label: "Pechuga" },
      { id: "des_mo_p4", label: "Carne en bistec" },
    ],
  }),
  mkCubiertos("des_mo"),
  mkComentario("des_mo"),
];

/** 9 — Tamal Santafereño (bebida + cubiertos) */
export const MOD_TAMAL: ModifierGroup[] = [
  mkModalidad("des_ta"),
  mkBebida("des_ta"),
  mkCubiertos("des_ta"),
  mkComentario("des_ta"),
];

/** Índice por producto cat_desay_0 … cat_desay_11 */
export const DESAYUNO_MODIFIERS_BY_INDEX: (ModifierGroup[] | undefined)[] = [
  MOD_DESAYUNO_CALDO("des_c0"),
  MOD_DESAYUNO_CALDO("des_c1"),
  MOD_DESAYUNO_CALDO("des_c2"),
  MOD_DESAYUNO_CALDO("des_c3"),
  MOD_DESAYUNO_COMPLETO,
  MOD_HUEVOS_GUSTO,
  MOD_MONONA,
  undefined,
  undefined,
  MOD_TAMAL,
  MOD_DESAYUNO_CALDO("des_c10"),
  MOD_DESAYUNO_CALDO("des_c11"),
];
